
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { TimeSlot, DayOfWeek } from '@/models/Schedule';
import { useTranslation } from 'react-i18next';
import { useTimeSlotCreateForm } from './forms/useTimeSlotCreateForm';
import TimeSlotCreateFormContent from './forms/TimeSlotCreateFormContent';
import TimeSlotCreateFormButtons from './forms/TimeSlotCreateFormButtons';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';

interface TimeSlotCreateFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateTimeSlots: (timeSlots: any[]) => Promise<void>;
  existingTimeSlots: TimeSlot[];
  preselectedDay?: DayOfWeek;
}

const TimeSlotCreateForm: React.FC<TimeSlotCreateFormProps> = ({
  open,
  onOpenChange,
  onCreateTimeSlots,
  existingTimeSlots,
  preselectedDay,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const { form, isSubmitting, isRecurringMode, onSubmit } = useTimeSlotCreateForm({
    existingTimeSlots,
    preselectedDay,
    open,
    onCreateTimeSlots,
    onOpenChange,
  });

  const formContent = (
    <TimeSlotCreateFormContent
      form={form}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      isRecurringMode={isRecurringMode}
    >
      <TimeSlotCreateFormButtons
        isSubmitting={isSubmitting}
        onCancel={() => onOpenChange(false)}
      />
    </TimeSlotCreateFormContent>
  );

  if (isMobile || isTablet) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="h-screen w-full rounded-none border-0 p-0 flex flex-col modal-safe-area-aware ios-modal-safe pwa-modal-header-fix"
        >
          <SheetHeader className="p-6 border-b flex-shrink-0 pt-[calc(1.5rem+env(safe-area-inset-top))] pwa-safe-header">
            <SheetTitle>{t('schedule.createTimeSlot')}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 p-6 overflow-hidden">{formContent}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('schedule.createTimeSlot')}</DialogTitle>
        </DialogHeader>

        {formContent}
      </DialogContent>
    </Dialog>
  );
};

export default TimeSlotCreateForm;
